import React, { useMemo } from "react";
import {
  View,
  ScrollView,
  TouchableOpacity,
  Animated,
  Text,
  Pressable,
  Image
} from "react-native";
import { ODSIcon } from "../../../openbrix-design-system/ui/atoms/ODSIcon";
import { ODSText } from "../../../openbrix-design-system/ui/atoms/ODSText";
import ODSButton from "../../../openbrix-design-system/ui/atoms/ODSButton";
import { formatDate } from "../../helpers/functionHelpers";
import { navigateAction } from "../../helpers/navigationHelpers";

// ✅ Types
export interface IMaintenanceItem {
  open_flow_maintenance_issue_id: number | string;
  title: string;
  status: string;
  category_id: number | string;
  room_type_id: number | string;
  date_opened: string;
  description: string;
  images: string[];
  videos: string[];
}

export interface ICategoryType {
  open_flow_maintenance_issue_category_id: number | string;
  name: string;
}

export interface IRoomType {
  open_flow_maintenance_issue_room_type_id: number | string;
  name: string;
}

interface IProps {
  data: IMaintenanceItem[];
  expandedId: number | string | null;
  onItemPress: (id: number | string) => void;
  roomTypes: IRoomType[];
  categoryTypes: ICategoryType[];
  flowId:string;
  navigation:any
}

// ✅ Status badge UI
const StatusBadge = ({ status }: { status: string }) => {
  const config = {
    Completed: { color: "#059669", bg: "#D1FAE5" },
    pending: { color: "#D97706", bg: "#FEF3C7" },
    PENDING: { color: "#DC2626", bg: "#FEE2E2" }
  }[status] || { color: "#6B7280", bg: "#F3F4F6" };

  return (
    <View style={{ paddingHorizontal: 10, paddingVertical: 6, borderRadius: 6, backgroundColor: config.bg,width:100,alignItems:"center" }}>
      <Text style={{ color: config.color, fontSize: 12, fontWeight: "600" }}>
        {status}
      </Text>
    </View>
  );
};

// ✅ Table row
const ExpandableTableRow = ({
  item,
  isExpanded,
  onPress,
  onEdit,
  roomTypes,
  categoryTypes
}: {
  item: IMaintenanceItem;
  isExpanded: boolean;
  onPress: () => void;
  onEdit: () => void;
  roomTypes: IRoomType[];
  categoryTypes: ICategoryType[];
}) => {

  const rotateAnim = useMemo(() => new Animated.Value(0), []);

  React.useEffect(() => {
    Animated.timing(rotateAnim, {
      toValue: isExpanded ? 1 : 0,
      duration: 250,
      useNativeDriver: true
    }).start();
  }, [isExpanded]);

  const rotate = rotateAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ["0deg", "180deg"]
  });

  const categoryName =
    categoryTypes.find((x) =>
      x.open_flow_maintenance_issue_category_id == item.category_id
    )?.name || "—";

  const roomName =
    roomTypes.find((x) =>
      x.open_flow_maintenance_issue_room_type_id == item.room_type_id
    )?.name || "—";

  return (
    <View style={{ borderBottomWidth: 1, borderBottomColor: "#E5E7EB" }}>
      
      {/* ✅ ROW */}
      <View
        style={{
          flexDirection: "row",
          paddingVertical: 14,
          paddingHorizontal: 16,
          alignItems: "center",
          backgroundColor: isExpanded ? "#F8FAFC" : "#FFFFFF"
        }}
      >
        {/* Expand Button */}
        <TouchableOpacity onPress={onPress} style={styles.colExpand}>
          <Animated.View style={{ transform: [{ rotate }] }}>
            <ODSIcon icon="Frame-3" size={20} />
          </Animated.View>
        </TouchableOpacity>

        {/* Columns */}
        <View style={styles.colId}>
          <ODSText type="sm">{item.open_flow_maintenance_issue_id}</ODSText>
        </View>

        <View style={styles.colTitle}>
          <ODSText type="sm">{item.title}</ODSText>
        </View>

        <View style={styles.colStatus}>
          <StatusBadge status={item?.status=="inprogress"?"In progress": item.status} />
        </View>

        <View style={styles.colDate}>
          <ODSText type="sm">{formatDate(item.date_opened)}</ODSText>
        </View>

        <View style={styles.colRoom}>
          <ODSText type="sm">{roomName}</ODSText>
        </View>

        <View style={styles.colCategory}>
          <ODSText type="sm">{categoryName}</ODSText>
        </View>

        <View style={styles.colActions}>
         {item?.status=="completed"?null: <ODSButton
            rIcon="TB-Edit"
            type="secondary"
            onPress={onEdit}
            restStyle={{ height: 26 }}
          >
            Edit
          </ODSButton>}
        </View>
      </View>

      {/* ✅ EXPANDED */}
      {isExpanded && (
        <View style={{ padding: 20, backgroundColor: "#FFFFFF" }}>
          {/* Description */}
          <Text style={styles.sectionTitle}>Description</Text>
          <Text style={styles.descText}>{item.description}</Text>

          {/* Media */}
          {(item.images.length > 0 || item.videos.length > 0) && (
  <>
    <Text style={styles.sectionTitle}>Attachments</Text>

    <View style={styles.mediaGrid}>
      {[...item.images.map((x, i) => ({ type: "img", url: x })),
        ...item.videos.map((x, i) => ({ type: "vid", url: x }))
      ].map((m, i) => (
        <Pressable
          key={i}
          onPress={() => window.open(m.url, "_blank")}
          style={styles.mediaCard}
        >
          {m.type === "img" ? (
            <Image
              source={{ uri: m.url }}
              style={{ width: 90, height: 90, borderRadius: 8 }}
              resizeMode="cover"
            />
          ) : (
            <Text style={{ fontSize: 40 }}>🎥</Text>
          )}
        </Pressable>
      ))}
    </View>
  </>
)}

        </View>
      )}
    </View>
  );
};

// ✅ Table header
const TableHeader = () => (
  <View style={styles.tableHeader}>
    <View style={styles.colExpand} />
    <Text style={[styles.header, styles.colId]}>ID</Text>
    <Text style={[styles.header, styles.colTitle]}>TITLE</Text>
    <Text style={[styles.header, styles.colStatus]}>STATUS</Text>
    <Text style={[styles.header, styles.colDate]}>DATE</Text>
    <Text style={[styles.header, styles.colRoom]}>ROOM</Text>
    <Text style={[styles.header, styles.colCategory]}>CATEGORY</Text>
    <Text style={[styles.header, styles.colActions]}>ACTIONS</Text>
  </View>
);

// ✅ MAIN COMPONENT
const WebMaintenanceView = (props: IProps) => {
  const { data, expandedId, onItemPress, roomTypes, categoryTypes,flowId,navigation } = props;

  return (
    <View style={styles.wrapper}>
      <TableHeader />

      <ScrollView style={{ maxHeight: 650 }} showsVerticalScrollIndicator={false}>
        {data.map((item) => (
          <ExpandableTableRow
            key={item.open_flow_maintenance_issue_id}
            item={item}
            isExpanded={expandedId === item.open_flow_maintenance_issue_id}
            onPress={() => onItemPress(item.open_flow_maintenance_issue_id)}
            onEdit={() =>  navigateAction(navigation, "Maintenance", { id:flowId,maintainance_id:item.open_flow_maintenance_issue_id})}
            roomTypes={roomTypes}
            categoryTypes={categoryTypes}
          />
        ))}
      </ScrollView>
    </View>
  );
};

export default WebMaintenanceView;

const styles = {
  wrapper: {
    backgroundColor: "#FFFFFF",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    overflow: "hidden",
  },

  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#F1F5F9",
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#E2E8F0",
    alignItems: "center",
  },

  header: {
    fontSize: 12,
    fontWeight: "700",
    color: "#475569",
    textTransform: "uppercase",
  },

  // ✅ FLEX COLUMNS (responsive)
  colExpand: { width: 40, justifyContent: "center", alignItems: "center" },
  colId: { flex: 0.7 },
  colTitle: { flex: 2 }, // ✅ Title gets most space
  colStatus: { flex: 1.4,alignItems:"center",textAlign:"center" },
  colDate: { flex: 1.4,alignItems:"center",textAlign:"center" },
  colRoom: { flex: 1.5 ,alignItems:"center",textAlign:"center"},
  colCategory: { flex: 1.5,alignItems:"center",textAlign:"center" },
  colActions: {
    flex: 1.2,
    justifyContent: "flex-end",
    flexDirection: "row",
    textAlign:"center"
  },

  sectionTitle: {
    fontSize: 15,
    fontWeight: "600",
    marginBottom: 8,
    color: "#1E293B",
  },

  descText: {
    fontSize: 14,
    color: "#475569",
    marginBottom: 20,
  },

  mediaGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },

  mediaCard: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F8FAFC",
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    minWidth: 140,
  },

  mediaText: {
    marginLeft: 8,
    fontSize: 12,
    color: "#334155",
  },
};
